export const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@givewise.ca'

export const TIMEZONE_OPTIONS = [
  { value: 'auto', label: 'Auto-detect from browser' },
  // North America - Pacific
  { value: 'America/Vancouver', label: 'Pacific Time - Vancouver' },
  { value: 'America/Los_Angeles', label: 'Pacific Time - Los Angeles' },
  { value: 'America/Tijuana', label: 'Pacific Time - Tijuana' },
  // North America - Mountain
  { value: 'America/Edmonton', label: 'Mountain Time - Edmonton' },
  { value: 'America/Denver', label: 'Mountain Time - Denver' },
  { value: 'America/Phoenix', label: 'Mountain Time - Phoenix (no DST)' },
  // North America - Central
  { value: 'America/Winnipeg', label: 'Central Time - Winnipeg' },
  { value: 'America/Chicago', label: 'Central Time - Chicago' },
  { value: 'America/Mexico_City', label: 'Central Time - Mexico City' },
  // North America - Eastern
  { value: 'America/Toronto', label: 'Eastern Time - Toronto' },
  { value: 'America/New_York', label: 'Eastern Time - New York' },
  // North America - Atlantic
  { value: 'America/Halifax', label: 'Atlantic Time - Halifax' },
  // North America - Newfoundland
  { value: 'America/St_Johns', label: 'Newfoundland Time - St. Johns' },
  // Europe
  { value: 'Europe/London', label: 'GMT/BST - London' },
  { value: 'Europe/Paris', label: 'CET - Paris' },
  { value: 'Europe/Berlin', label: 'CET - Berlin' },
  { value: 'Europe/Amsterdam', label: 'CET - Amsterdam' },
  { value: 'Europe/Zurich', label: 'CET - Zurich' },
  // Asia Pacific
  { value: 'Asia/Tokyo', label: 'JST - Tokyo' },
  { value: 'Asia/Hong_Kong', label: 'HKT - Hong Kong' },
  { value: 'Asia/Singapore', label: 'SGT - Singapore' },
  { value: 'Asia/Sydney', label: 'AEST - Sydney' },
  { value: 'Pacific/Auckland', label: 'NZST - Auckland' },
  // UTC
  { value: 'UTC', label: 'UTC' },
]

export const SIGNUP_TYPE = {
  personal: 'personal',
  business: 'business',
  charity: 'charity',
  advisor: 'advisor',
}

export const CHARITY_STATUS_OVERRIDE = {
  noOverride: 'no override',
  active: 'active',
  inactive: 'inactive',
}

export const PAYMENT_TYPE = {
  card: 'card',
  cheque: 'cheque',
  eft: 'eft',

  securities: 'securities',
  otherNonCash: 'other-non-cash',

  dafTransferInternal: 'internal-daf-transfer',
  dafTransferThirdParty: 'third-party-daf-transfer',
  dafTransferFoundation: 'foundation-to-daf-transfer',
  dafOther: 'daf-other',
  transferFromFoundation: 'transfer-from-foundation',

  bankTransfer: 'bank-transfer',
  applePay: 'apple-pay',
  cryptocurrency: 'cryptocurrency',
}

// Numeric payment type IDs from database
// NOTE: Must stay in sync with api/src/contributions/enums/contribution-payment-methods.ts
export const PAYMENT_TYPE_ID = {
  card: 1,
  cheque: 2,
  eft: 3,
  securities: 4,
  otherNonCash: 5,
  dafTransferInternal: 6,
  dafTransferThirdParty: 7,
  dafTransferFoundation: 8,
  dafOther: 9,
  transferFromFoundation: 10,
  bankTransfer: 11,
  applePay: 12,
  cryptocurrency: 13,
}

// Display names for numeric payment type IDs
export const PAYMENT_TYPE_ID_DISPLAY = {
  [PAYMENT_TYPE_ID.card]: 'Card',
  [PAYMENT_TYPE_ID.cheque]: 'Cheque',
  [PAYMENT_TYPE_ID.eft]: 'e-Transfer',
  [PAYMENT_TYPE_ID.securities]: 'Securities',
  [PAYMENT_TYPE_ID.otherNonCash]: 'Other - Non Cash',
  [PAYMENT_TYPE_ID.dafTransferInternal]: 'DAF Transfer - Internal',
  [PAYMENT_TYPE_ID.dafTransferThirdParty]: 'DAF Transfer - Third Party',
  [PAYMENT_TYPE_ID.dafTransferFoundation]: 'DAF Transfer - Foundation',
  [PAYMENT_TYPE_ID.dafOther]: 'DAF - Other',
  [PAYMENT_TYPE_ID.transferFromFoundation]: 'Transfer from Foundation',
  [PAYMENT_TYPE_ID.bankTransfer]: 'Bank Transfer',
  [PAYMENT_TYPE_ID.applePay]: 'Apple Pay',
  [PAYMENT_TYPE_ID.cryptocurrency]: 'Cryptocurrency',
}

// Display names for string payment type slugs (legacy format)
export const PAYMENT_TYPE_SLUG_DISPLAY = {
  [PAYMENT_TYPE.card]: 'Card',
  [PAYMENT_TYPE.cheque]: 'Cheque',
  [PAYMENT_TYPE.eft]: 'e-Transfer',
  [PAYMENT_TYPE.securities]: 'Securities',
  [PAYMENT_TYPE.otherNonCash]: 'Other - Non Cash',
  [PAYMENT_TYPE.dafTransferInternal]: 'DAF Transfer - Internal',
  [PAYMENT_TYPE.dafTransferThirdParty]: 'DAF Transfer - Third Party',
  [PAYMENT_TYPE.dafTransferFoundation]: 'DAF Transfer - Foundation',
  [PAYMENT_TYPE.dafOther]: 'DAF - Other',
  [PAYMENT_TYPE.transferFromFoundation]: 'Transfer from Foundation',
  [PAYMENT_TYPE.bankTransfer]: 'Bank Transfer',
  [PAYMENT_TYPE.applePay]: 'Apple Pay',
  [PAYMENT_TYPE.cryptocurrency]: 'Cryptocurrency',
}

// Helper to get display name from either numeric ID or string slug
export const getPaymentTypeDisplay = (paymentType) => {
  if (paymentType == null) return null
  return PAYMENT_TYPE_ID_DISPLAY[paymentType] || PAYMENT_TYPE_SLUG_DISPLAY[paymentType] || paymentType
}

// Payment type choices for admin filters
export const PAYMENT_TYPES = [
  { id: PAYMENT_TYPE_ID.card, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.card] },
  { id: PAYMENT_TYPE_ID.cheque, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.cheque] },
  { id: PAYMENT_TYPE_ID.eft, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.eft] },
  { id: PAYMENT_TYPE_ID.securities, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.securities] },
  { id: PAYMENT_TYPE_ID.otherNonCash, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.otherNonCash] },
  { id: PAYMENT_TYPE_ID.dafTransferInternal, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.dafTransferInternal] },
  { id: PAYMENT_TYPE_ID.dafTransferThirdParty, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.dafTransferThirdParty] },
  { id: PAYMENT_TYPE_ID.dafTransferFoundation, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.dafTransferFoundation] },
  { id: PAYMENT_TYPE_ID.dafOther, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.dafOther] },
  { id: PAYMENT_TYPE_ID.transferFromFoundation, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.transferFromFoundation] },
  { id: PAYMENT_TYPE_ID.bankTransfer, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.bankTransfer] },
  { id: PAYMENT_TYPE_ID.applePay, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.applePay] },
  { id: PAYMENT_TYPE_ID.cryptocurrency, name: PAYMENT_TYPE_ID_DISPLAY[PAYMENT_TYPE_ID.cryptocurrency] },
]

export const USER_STATUS_ID = {
  guest: 1,
  unconfirmed: 2,
  active: 3,
  disengaged: 4,
  archived: 5,
  blocked: 6,
  invited: 7,
  closed: 8,
}
export const USER_STATUS = {
  [USER_STATUS_ID.guest]: 'Guest',
  [USER_STATUS_ID.unconfirmed]: 'Unconfirmed',
  [USER_STATUS_ID.active]: 'Active',
  [USER_STATUS_ID.disengaged]: 'Disengaged',
  [USER_STATUS_ID.archived]: 'Archived',
  [USER_STATUS_ID.blocked]: 'Blocked',
  [USER_STATUS_ID.invited]: 'Invited',
  [USER_STATUS_ID.closed]: 'Closed',
}

export const CHARITY_INVITE_STATUS_ID = {
  invited: 1,
  accepted: 2,
  revoked: 3,
  unconfirmed: 4,
}
export const CHARITY_INVITE_STATUS = {
  [CHARITY_INVITE_STATUS_ID.invited]: 'Invited',
  [CHARITY_INVITE_STATUS_ID.accepted]: 'Accepted',
  [CHARITY_INVITE_STATUS_ID.revoked]: 'Revoked',
  [CHARITY_INVITE_STATUS_ID.unconfirmed]: 'Unconfirmed',
}

export const PAYMENT_TYPE_DISPLAY = {
  [PAYMENT_TYPE.card]: 'Card',
  [PAYMENT_TYPE.cheque]: 'Cheque',
  [PAYMENT_TYPE.eft]: 'e-Transfer',

  [PAYMENT_TYPE.securities]: 'Securities',
  [PAYMENT_TYPE.otherNonCash]: 'Other - Non Cash',

  [PAYMENT_TYPE.dafTransferInternal]: 'DAF Transfer - Internal',
  [PAYMENT_TYPE.dafTransferThirdParty]: 'DAF Transfer - Third Party',
  [PAYMENT_TYPE.dafTransferFoundation]: 'DAF Transfer - Foundation',
  [PAYMENT_TYPE.dafOther]: 'DAF - Other',
  [PAYMENT_TYPE.transferFromFoundation]: 'Transfer from Foundation',

  [PAYMENT_TYPE.bankTransfer]: 'Bank Transfer',
  [PAYMENT_TYPE.applePay]: 'Apple Pay',
}

export const CORPORATION_USER_TYPE = 'corporation'
export const INDIVIDUAL_USER_TYPE = 'individual'

export const MANAGING_USER_ROLE = [{ id: 'manager', name: 'Manager' }]

export const PROVINCES_LIST = [
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
]

export const PROVINCE_ABBREVIATION_MAP = {
  // Canadian Provinces
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NT: 'Northwest Territories',
  NS: 'Nova Scotia',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
  // US States
  AL: 'Alabama',
  AK: 'Alaska',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  FL: 'Florida',
  GA: 'Georgia',
  HI: 'Hawaii',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
  DC: 'District of Columbia',
}

export const GRANT_STATES_ENUM = {
  open: 1,
  openPending: 2,
  processing: 3,
  canceled: 5,
  sent: 6,
  failed: 8,
  reissued: 9,
  failedPending: 10,
}

export const GRANT_STATES_LABELS = {
  [GRANT_STATES_ENUM.open]: 'Open',
  [GRANT_STATES_ENUM.openPending]: 'Open: Pending',
  [GRANT_STATES_ENUM.processing]: 'Processing',
  [GRANT_STATES_ENUM.canceled]: 'Canceled',
  [GRANT_STATES_ENUM.sent]: 'Sent',
  [GRANT_STATES_ENUM.failed]: 'Failed',
  [GRANT_STATES_ENUM.reissued]: 'Reissued',
  [GRANT_STATES_ENUM.failedPending]: 'Failed: Pending',
}

export const COUNTRY_LIST = [
  'Canada',
  'Afghanistan',
  'Aland Islands',
  'Albania',
  'Algeria',
  'American Samoa',
  'Andorra',
  'Angola',
  'Anguilla',
  'Antarctica',
  'Antigua and Barbuda',
  'Argentina',
  'Armenia',
  'Aruba',
  'Australia',
  'Austria',
  'Azerbaijan',
  'Bahamas',
  'Bahrain',
  'Bangladesh',
  'Barbados',
  'Belarus',
  'Belgium',
  'Belize',
  'Benin',
  'Bermuda',
  'Bhutan',
  'Bolivia',
  'Bonaire, Sint Eustatius and Saba',
  'Bosnia and Herzegovina',
  'Botswana',
  'Bouvet Island',
  'Brazil',
  'British Indian Ocean Territory',
  'Brunei Darussalam',
  'Bulgaria',
  'Burkina Faso',
  'Burundi',
  'Cambodia',
  'Cameroon',
  'Cape Verde',
  'Cayman Islands',
  'Central African Republic',
  'Chad',
  'Chile',
  'China',
  'Christmas Island',
  'Cocos (Keeling) Islands',
  'Colombia',
  'Comoros',
  'Congo',
  'Congo, Democratic Republic of the Congo',
  'Cook Islands',
  'Costa Rica',
  "Cote D'Ivoire",
  'Croatia',
  'Cuba',
  'Curacao',
  'Cyprus',
  'Czech Republic',
  'Denmark',
  'Djibouti',
  'Dominica',
  'Dominican Republic',
  'Ecuador',
  'Egypt',
  'El Salvador',
  'Equatorial Guinea',
  'Eritrea',
  'Estonia',
  'Ethiopia',
  'Falkland Islands (Malvinas)',
  'Faroe Islands',
  'Fiji',
  'Finland',
  'France',
  'French Guiana',
  'French Polynesia',
  'French Southern Territories',
  'Gabon',
  'Gambia',
  'Georgia',
  'Germany',
  'Ghana',
  'Gibraltar',
  'Greece',
  'Greenland',
  'Grenada',
  'Guadeloupe',
  'Guam',
  'Guatemala',
  'Guernsey',
  'Guinea',
  'Guinea-Bissau',
  'Guyana',
  'Haiti',
  'Heard Island and Mcdonald Islands',
  'Holy See (Vatican City State)',
  'Honduras',
  'Hong Kong',
  'Hungary',
  'Iceland',
  'India',
  'Indonesia',
  'Iran, Islamic Republic of',
  'Iraq',
  'Ireland',
  'Isle of Man',
  'Israel',
  'Italy',
  'Jamaica',
  'Japan',
  'Jersey',
  'Jordan',
  'Kazakhstan',
  'Kenya',
  'Kiribati',
  "Korea, Democratic People's Republic of",
  'Korea, Republic of',
  'Kosovo',
  'Kuwait',
  'Kyrgyzstan',
  "Lao People's Democratic Republic",
  'Latvia',
  'Lebanon',
  'Lesotho',
  'Liberia',
  'Libyan Arab Jamahiriya',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Macao',
  'Macedonia, the Former Yugoslav Republic of',
  'Madagascar',
  'Malawi',
  'Malaysia',
  'Maldives',
  'Mali',
  'Malta',
  'Marshall Islands',
  'Martinique',
  'Mauritania',
  'Mauritius',
  'Mayotte',
  'Mexico',
  'Micronesia, Federated States of',
  'Moldova, Republic of',
  'Monaco',
  'Mongolia',
  'Montenegro',
  'Montserrat',
  'Morocco',
  'Mozambique',
  'Myanmar',
  'Namibia',
  'Nauru',
  'Nepal',
  'Netherlands',
  'Netherlands Antilles',
  'New Caledonia',
  'New Zealand',
  'Nicaragua',
  'Niger',
  'Nigeria',
  'Niue',
  'Norfolk Island',
  'Northern Mariana Islands',
  'Norway',
  'Oman',
  'Pakistan',
  'Palau',
  'Palestinian Territory, Occupied',
  'Panama',
  'Papua New Guinea',
  'Paraguay',
  'Peru',
  'Philippines',
  'Pitcairn',
  'Poland',
  'Portugal',
  'Puerto Rico',
  'Qatar',
  'Reunion',
  'Romania',
  'Russian Federation',
  'Rwanda',
  'Saint Barthelemy',
  'Saint Helena',
  'Saint Kitts and Nevis',
  'Saint Lucia',
  'Saint Martin',
  'Saint Pierre and Miquelon',
  'Saint Vincent and the Grenadines',
  'Samoa',
  'San Marino',
  'Sao Tome and Principe',
  'Saudi Arabia',
  'Senegal',
  'Serbia',
  'Serbia and Montenegro',
  'Seychelles',
  'Sierra Leone',
  'Singapore',
  'Sint Maarten',
  'Slovakia',
  'Slovenia',
  'Solomon Islands',
  'Somalia',
  'South Africa',
  'South Georgia and the South Sandwich Islands',
  'South Sudan',
  'Spain',
  'Sri Lanka',
  'Sudan',
  'Suriname',
  'Svalbard and Jan Mayen',
  'Swaziland',
  'Sweden',
  'Switzerland',
  'Syrian Arab Republic',
  'Taiwan, Province of China',
  'Tajikistan',
  'Tanzania, United Republic of',
  'Thailand',
  'Timor-Leste',
  'Togo',
  'Tokelau',
  'Tonga',
  'Trinidad and Tobago',
  'Tunisia',
  'Turkey',
  'Turkmenistan',
  'Turks and Caicos Islands',
  'Tuvalu',
  'Uganda',
  'Ukraine',
  'United Arab Emirates',
  'United Kingdom',
  'United States',
  'United States Minor Outlying Islands',
  'Uruguay',
  'Uzbekistan',
  'Vanuatu',
  'Venezuela',
  'Viet Nam',
  'Virgin Islands, British',
  'Virgin Islands, U.S.',
  'Wallis and Futuna',
  'Western Sahara',
  'Yemen',
  'Zambia',
  'Zimbabwe',
]
